import { createTheme } from '@mui/material';

import { breakpoints, getTypography, normalizeFont } from './Typography';

const roundHalfDown = (x: number): number => (Math.abs((x % 1) - 0.5) < 1e-9 ? Math.floor(x) : Math.round(x));

const tintHex = (hex: string, tintPercent: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const t = tintPercent / 100;
    const w = 1 - t;
    const blend = (c: number) => roundHalfDown(c * t + 255 * w);
    return `#${blend(r).toString(16).padStart(2, '0')}${blend(g).toString(16).padStart(2, '0')}${blend(b).toString(16).padStart(2, '0')}`;
};

// Color name to hex mapping
const colorNameToHex: { [key: string]: string } = {
    red: '#FF0000',
    green: '#008000',
    blue: '#0000FF',
    yellow: '#FFFF00',
    orange: '#FFA500',
    purple: '#800080',
    pink: '#FFC0CB',
    black: '#000000',
    white: '#FFFFFF',
    gray: '#808080',
    grey: '#808080',
    brown: '#A52A2A',
    cyan: '#00FFFF',
    magenta: '#FF00FF',
    lime: '#00FF00',
    maroon: '#800000',
    navy: '#000080',
    olive: '#808000',
    teal: '#008080',
    silver: '#C0C0C0',
    gold: '#FFD700',
    indigo: '#4B0082',
    violet: '#EE82EE',
    turquoise: '#40E0D0',
    coral: '#FF7F50',
    salmon: '#FA8072',
    khaki: '#F0E68C',
    lavender: '#E6E6FA',
    plum: '#DDA0DD',
    tomato: '#FF6347',
    crimson: '#DC143C',
    darkred: '#8B0000',
    firebrick: '#B22222',
    lightcoral: '#F08080',
    darkgreen: '#006400',
    forestgreen: '#228B22',
    seagreen: '#2E8B57',
    mediumseagreen: '#3CB371',
    lightgreen: '#90EE90',
    palegreen: '#98FB98',
    darkblue: '#00008B',
    mediumblue: '#0000CD',
    lightblue: '#ADD8E6',
    skyblue: '#87CEEB',
    steelblue: '#4682B4',
    darkgray: '#A9A9A9',
    lightgray: '#D3D3D3',
    dimgray: '#696969',
};

/**
 * Converts a color name or hex code to a valid hex color code
 * @param color - Color name (e.g., "red") or hex code (e.g., "#FF0000")
 * @returns Valid hex color code
 */
const normalizeColor = (color: string): string => {
    if (!color) return '#001E60'; // Default fallback

    // Trim whitespace and convert to lowercase
    const normalizedColor = color.trim().toLowerCase();

    // Check if it's already a valid hex code
    if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(normalizedColor)) {
        return normalizedColor;
    }

    // Check if it's a color name and convert to hex
    if (colorNameToHex[normalizedColor]) {
        return colorNameToHex[normalizedColor];
    }

    // If it's not recognized, try to create a hex from it
    // This handles cases where the API might return something unexpected
    console.warn(`Unknown color: "${color}". Using default color.`);
    return '#001E60'; // Default fallback
};

// Base colors object (static colors that don't change)
const baseColors = {
    fullWhite: '#FFFFFF',
    fullBlack: '#000000',
    greyPreset: {
        2.5: '#fbfcff',
        5: '#F3F4F6',
        10: '#A1AFA5',
        20: '#DBE0DC',
        30: '#C9D1CB',
        40: '#8A9B8E',
        50: '#4D4D4D',
        60: '#3A413C',
        80: '#4C554E',
        90: '#F3F5F4',
        100: '#6B7280',
        130: '#A6A6A6',
        140: '#B1BCB3',
        150: '#626E65',
    },
    warningPreset: {
        10: '#8B6700',
        20: '#B28400',
        30: '#C49100',
        40: '#D0A733',
        50: '#E87722',
        60: '#FDF1E9',
        70: '#B85E14',
        80: '#F8D5BA',
        90: '#FFE8D6',
        100: '#fff8f2',
    },
    errorPreset: {
        10: '#AC1111',
        20: '#F7E7E7',
        30: '#E5B5B5',
        40: '#7A0C0C',
    },
    successPreset: {
        10: '#44883E',
        20: '#C5DAC3',
        30: '#30612C',
        40: '#ECF3EC',
        50: '#3E7C38',
    },
    bluePreset: {
        10: '#001E60', // Default value, will be overridden by normalizedColor in getColors()
        30: '#1F6CC9',
        40: '#699DDB',
        50: '#E9F0FA',
        60: '#BAD1EE',
        70: '#4C89D4',
        80: '#dcecff',
        90: '#007396',
        100: '#B0D4DE',
        110: '#E6F1F5',
    },
};

/**
 * Generates lighter color variants from a primary color
 * @param primaryColor - Primary color (hex or color name)
 * @returns Object with 5%, 15%, 30%, and 70% lighter variants
 */
export const getPrimaryLightVariants = (primaryColor: string) => {
    const hex = normalizeColor(primaryColor);
    return {
        5: tintHex(hex, 5),
        15: tintHex(hex, 15),
        30: tintHex(hex, 30),
        70: tintHex(hex, 70),
        100: tintHex(hex, 100),
    };
};

/**
 * Creates colors object with dynamic primary color for bluePreset[10]
 * @param primaryColor - Primary color to use for bluePreset[10] (will be normalized)
 * @returns Colors object with updated bluePreset[10] and primaryLight variants
 */
export const getColors = (primaryColor: string = '#001E60') => {
    const normalizedColor = normalizeColor(primaryColor);
    const primaryLight = getPrimaryLightVariants(normalizedColor);
    return {
        ...baseColors,
        bluePreset: {
            ...baseColors.bluePreset,
            10: normalizedColor, // Assign normalizedColor to bluePreset[10]
        },
        primaryLight,
    };
};

// Current app colors – updated whenever createAppTheme is called (e.g. when brand primary/font changes).
// Use this globally: colors.primaryLight, colors.bluePreset, etc. No need for useTheme() for these.
let currentAppColors = getColors();

export const colors = new Proxy({} as ReturnType<typeof getColors>, {
    get(_, prop) {
        return (currentAppColors as Record<string | symbol, unknown>)[prop];
    },
});

export const createAppTheme = (primaryColor: string = '#001E60', font: string | number | undefined = undefined) => {
    // Normalize the color to ensure it's a valid hex code
    const normalizedColor = normalizeColor(primaryColor);
    // Normalize the font (handles index like "4" or font name like "Montserrat")
    const normalizedFont = normalizeFont(font);
    // Get colors with updated primary color
    const themeColors = getColors(normalizedColor);
    currentAppColors = themeColors;
    const typography = getTypography(normalizedFont);

    return createTheme({
        spacing: 8,
        palette: {
            primary: {
                main: normalizedColor,
                //   light: '',
                //   dark: '',
                //   contrastText: '',
            },
            primaryLight: themeColors.primaryLight,
            text: {
                primary: colors.fullBlack,
                secondary: colors.greyPreset[80],
                disabled: colors.greyPreset[150],
            },
        },
        typography,

        shadows: [
            'none',
            '0px 14px 20px 0px #00000026',
            '0px 14px 20px 0px #00000026',
            'none',
            '0px 4px 4px 0px #0000000F',
            '0px 2px 2px 0px #0000001A',
            '4px 0px 4px 0px #0000000D inset',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
            'none',
        ],
        components: {
            // Global focus styles for all button based components
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        '&:focus-visible': {
                            outline: `2px solid ${colors.fullBlack}`,
                            outlineOffset: '2px',
                            // boxShadow: `0 0 0 4px ${colors.primaryLight[30]}`,
                            borderRadius: '4px',
                        },
                    },
                },
            },

            // Mobile browsers auto-zoom on focus when an input's font size is below 16px
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        [breakpoints.down('sm')]: {
                            fontSize: 16,
                        },
                    },
                },
            },

            // Global focus styles for text fields / inputs
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                            borderColor: colors.fullBlack,
                            // boxShadow: `0 0 0 4px ${colors.primaryLight[30]}`,
                        },

                        '&:focus-within': {
                            outlineOffset: '2px',
                            borderRadius: '4px',
                        },
                    },
                },
            },

            // Global focus styles for links
            MuiLink: {
                styleOverrides: {
                    root: {
                        color: themeColors.bluePreset[30],

                        '&:hover': {
                            color: themeColors.bluePreset[10],
                        },

                        '&:visited': {
                            color: themeColors.bluePreset[30],
                        },

                        '&:active': {
                            color: themeColors.bluePreset[30],
                        },

                        '&:focus-visible': {
                            outline: `2px solid ${colors.fullBlack}`,
                            outlineOffset: '2px',
                            // boxShadow: `0 0 0 4px ${colors.primaryLight[30]}`,
                            borderRadius: '2px',
                        },
                    },
                },
            },

            // Radio styles
            MuiRadio: {
                styleOverrides: {
                    root: {
                        color: themeColors.greyPreset[30],
                        padding: '9px',

                        '&:hover': {
                            backgroundColor: 'transparent',
                        },

                        '& .MuiSvgIcon-root': {
                            fontSize: 24,
                            width: 24,
                            height: 24,
                        },

                        '&.Mui-checked': {
                            color: themeColors.warningPreset[30],

                            '& .MuiSvgIcon-root': {
                                filter: 'none',
                            },
                        },

                        '&:focus-visible': {
                            outline: `2px solid ${colors.fullBlack}`,
                            outlineOffset: '2px',
                            // boxShadow: `0 0 0 4px ${colors.primaryLight[30]}`,
                            borderRadius: '50%',
                        },
                    },
                },
            },

            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        ...typography.body2,
                        color: themeColors.bluePreset[10],
                        fontFamily: normalizedFont,
                        fontWeight: 400,
                    },
                },
            },

            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        '&.Mui-disabled': {
                            opacity: 1,
                        },

                        '&:focus-visible': {
                            outline: `2px solid ${colors.fullBlack}`,
                            outlineOffset: '-2px',
                            // boxShadow: `0 0 0 4px ${colors.primaryLight[30]}`,
                        },
                    },
                },
            },
        },
    });
};

// Default theme export for backward compatibility
export const theme = createAppTheme();

// Extend MUI palette so primaryLight is available globally via theme.palette.primaryLight
declare module '@mui/material/styles' {
    interface Palette {
        primaryLight: {
            5: string;
            15: string;
            30: string;
            70: string;
        };
    }
    interface PaletteOptions {
        primaryLight?: {
            5: string;
            15: string;
            30: string;
            70: string;
        };
    }
}
